import Parse from 'parse'
import config from 'config'
import { HttpLink, from, ApolloLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { getABTestFraction } from '../../common/analytics/abTest'
import { getRegion } from '../../common/region/region'
import { getInstallationId } from '../hooks/useInstallationId'
import { sentryLink } from './sentryLink'
import { invalidSessionLink } from './invalidSessionLink'

const withInstallationId = () =>
  setContext(async (_, { headers }) => {
    const installationId = await getInstallationId()
    if (installationId) {
      return {
        headers: { 'X-Parse-Installation-Id': installationId, ...headers }
      }
    }
    return { headers }
  })

const withAuth = setContext(async (_, { headers }) => {
  // Use Parse.User.current() instead of getCurrentParseUser() to avoid circular dependency
  // eslint-disable-next-line no-restricted-syntax
  const parseUser = Parse.User.current()
  if (parseUser) {
    return {
      headers: { 'X-Parse-Session-Token': parseUser.getSessionToken(), ...headers }
    }
  } else {
    return { headers }
  }
})

const withAbTestFraction = setContext((_, { headers }) => {
  let abTestFraction
  try {
    abTestFraction = getABTestFraction()
  } catch (e) {
    console.error('Error getting AB test fraction:', e)
    return { headers }
  }
  return {
    headers: { 'client-ab-test-fraction': abTestFraction, ...headers }
  }
})

const withRegion = setContext((_, { headers }) => ({
  headers: { region: getRegion(), ...headers }
}))

const withParseSetup = () =>
  from([
    withInstallationId(),
    withAuth,
    withAbTestFraction,
    withRegion,
    sentryLink,
    invalidSessionLink,
    new HttpLink({
      // Operation name parameter is to improve searchablility in the network tab and logs
      uri: ({ operationName }) => `${config.apollo.uri}?_=${operationName}`,
      headers: {
        'X-Parse-Application-Id': config.parse.applicationId,
        'Content-Type': 'application/json'
      },
      fetch,
      fetchOptions: {
        method: 'POST'
      }
    })
  ])

export const sanityOrParseLink =
  // Go directly to sanity API (not CDN) if preview is enabled
  ApolloLink.split(
    (operation) => operation.getContext().api === 'sanity' && operation.getContext().preview,
    new HttpLink({
      uri: `https://${config.sanity.projectId}.api.sanity.io/v1/graphql/${config.sanity.dataset}/${config.sanity.version}`,
      fetch,
      fetchOptions: {
        method: 'POST'
      },
      headers: {
        'Content-Type': 'application/json',
        'Accept-Encoding': 'gzip'
      },
      credentials: 'include'
    }),
    // Otherwise, go to sanity CDN
    ApolloLink.split(
      (operation) => operation.getContext().api === 'sanity' && !operation.getContext().preview,
      new HttpLink({
        uri: `https://${config.sanity.projectId}.apicdn.sanity.io/v1/graphql/${config.sanity.dataset}/${config.sanity.version}`,
        fetch
      }),
      // Otherwise, go to Parse
      withParseSetup()
    )
  )
